import React, { FC, useState, useEffect } from "react";

import {
  Table,
  Row,
  Col,
  Button,
  Input,
  message,
  Drawer,
  notification,
  Select,
  Space,
  Modal,
} from "antd";

import { DeleteOutlined, ExclamationCircleOutlined, OrderedListOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { PagedResult } from "states/paged-result-model";
import { useDebouncedCallback } from "use-debounce/lib";

import {
  RemovedBlogModel,
  SearchBlogRequestModel,
  BlogModel,
} from "states/blog/blog.models";
import { removeBlog, searchBlog, updateSortBlogApi, updateSortListBlogApi } from "states/blog/blog.api";
import { Link } from "react-router-dom";
import { BlogCategoryModel } from "states/blog-category/blog-category.models";
import { searchBlogCategory } from "states/blog-category/blog-category.api";
import {
  CoreResource,
  momentDateTimeFormat,
  t,
} from "shared/plugins/localization";

import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "app.store";
import momentTZ from "moment-timezone";
import { Sortable, SortItemModel } from "shared/components/sortable/sortable";
import { UpdatedSortEntityModel } from "shared/models/updated-sort-entity-model";
interface IBlogProps { }
const { Option } = Select;
const Blog: FC<IBlogProps> = () => {
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<RemovedBlogModel>({
    idList: [],
  });
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isUpdateSort, setIsUpdateSort] = useState(false);
  const [pagedResults, setPagedResults] = useState(
    {} as PagedResult<BlogModel>
  );

  const currentTimeZone = useSelector(
    (state: RootState) => state.globalValue.ianaTimeZone,
    shallowEqual
  );

  const [filterModel, setFilterModel] = useState<SearchBlogRequestModel>({
    page: 1,
    pageSize: 20,
  });
  const [blogCategories, setBlogCategories] = useState<BlogCategoryModel[]>([]);
  const rowSelection = {
    selectedRowsKeys,
    onChange: (selectedRowKeys: any) => {
      setSelectedRowsKeys({ ...selectedRowsKeys, idList: selectedRowKeys });
    },
  };
  const handleSortListBlog = async (sortList: SortItemModel[]) => {
    try {
      let payload = sortList.map(
        (item) =>
        ({
          id: Number(item.key),
          sort: item.sort,
        } as UpdatedSortEntityModel<number>)
      );
      await updateSortListBlogApi(payload);
      setIsUpdateSort(false);
      handleSearch();
    } catch {
      message.error("Lỗi hệ thống");
    }
  };
  const handleSortBlog = async (sort: SortItemModel) => {
    try {
      let payload = {
        id: Number(sort.key),
        sort: sort.sort,
      } as UpdatedSortEntityModel<number>;
      await updateSortBlogApi(payload);
    } catch {
      message.error("Lỗi hệ thống");
    }
  };
  const getBlogCategories = async () => {
    try {
      const res = await searchBlogCategory({
        name: "",
        page: 1,
        pageSize: 1000,
      });
      setBlogCategories(res.data.results);
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
        "Lỗi hệ thống"
      );
    }
  };
  const onEmptySelection = () => {
    setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
  };

  const gotoPage = async (page: number,pageSize:number) => {
    setIsLoadingList(true);
    const result = await searchBlog({ ...filterModel, page,pageSize });
    result.data.results.forEach((m) => (m.key = m.id.toString()));
    setPagedResults(result.data);
    setIsLoadingList(false);
  };
  const handleSearch = useDebouncedCallback(() => {
    gotoPage(1,20);
  }, 500);
  const handleUpdateSort = useDebouncedCallback((sort: SortItemModel) => {
    handleSortBlog(sort);
  }, 500);

  const onRemoveBtn = () => {
    Modal.confirm({
      title: 'Thông báo',
      icon: <ExclamationCircleOutlined />,
      content: 'Bạn có muốn xóa bài viết này không?',
      okText: 'Đồng ý',
      cancelText: 'Hủy bỏ',
      onOk: async () => {
        try {
          await removeBlog(selectedRowsKeys);
          setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
          handleSearch();
          message.success("Cập nhật thành công");
        } catch (error) {
          message.error(
            error?.response?.data?.error?.message ??
            "Lỗi hệ thống"
          );
        }
      }
    });
  };

  const columns: ColumnsType<BlogModel> = [
    {
      title: "Id",
      dataIndex: "id",
      key:"id",
      width:"40px",
      render: (id: string, item) => (
        <React.Fragment>
          <Link to={`/admin/blog/update/${item.id}`}>{id}</Link>
        </React.Fragment>
      ),
    },
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      render: (title: string, item) => (
        <React.Fragment>
          <Link to={`/admin/blog/update/${item.id}`}>{title}</Link>
        </React.Fragment>
      ),
    },
    {
      title: "Đường dẫn",
      dataIndex: "slug",
      key: "slug",
      render: (slug: string) => <React.Fragment>{slug}</React.Fragment>,
    },
    {
      title: "Lượt xem",
      dataIndex: "viewCount",
      key: "viewCount",
      render: (viewCount: number) => <React.Fragment>{viewCount}</React.Fragment>,
    },
    {
      title: "Ngày tạo",
      dataIndex: "creationTime",
      key: "creationTime",
      render: (creationTime: Date) => (
        <React.Fragment>
          {currentTimeZone && creationTime && momentTZ(creationTime)
            .tz(currentTimeZone)
            .format(momentDateTimeFormat())}
        </React.Fragment>
      ),
    },
    {
      title: "Lần cập nhật cuối",
      dataIndex: "lastModificationTime",
      key: "lastModificationTime",
      render: (lastModificationTime: Date) => (
        <React.Fragment>
          {lastModificationTime && currentTimeZone && momentTZ(lastModificationTime ?? new Date())
            .tz(currentTimeZone)
            .format(momentDateTimeFormat())}
        </React.Fragment>
      ),
    },
    {
      title: "Sắp xếp",
      dataIndex: "sort",
      key: "sort",
      render: (sort: number, item) => <React.Fragment>
        <Input
          value={sort ?? 0}
          onChange={(e) => {
            handleSortBlog({
              key: item.id.toString(),
              sort: Number(e.target.value)
            } as SortItemModel)
            item.sort = Number(e.target.value);
            setPagedResults({ ...pagedResults })
          }}
        ></Input>
      </React.Fragment>,
    },
  ];

  useEffect(() => {
    handleSearch();
  }, [filterModel]);
  useEffect(() => {
    getBlogCategories();
  }, []);
  return (
    <div className="blog">
      <Row gutter={[16, 16]} justify="space-between" className="mb-4">
        <Col span={4}>
          <Input.Search
            value={filterModel.title}
            placeholder={`Tìm theo tiêu đề`}
            onChange={(e) => {
              setFilterModel({ ...filterModel, title: e.target.value });
            }}
          ></Input.Search>
        </Col>
        <Col span={4}>
          <Select
            className="w-100"
            placeholder={`Tìm theo danh mục`}
            onChange={(value: number) => {
              setFilterModel({ ...filterModel, blogCategoryId: value });
            }}
            allowClear
            value={filterModel?.blogCategoryId}
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {blogCategories &&
              blogCategories.map((blogCate) => (
                <Option key={blogCate.id} value={blogCate.id}>
                  {blogCate.name}
                </Option>
              ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            className="w-100"
            placeholder={`Sắp xếp`}
            onChange={(value: string) => {
              setFilterModel({ ...filterModel, sort: value });
            }}
            allowClear
            value={filterModel?.sort}
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value={`Ascending`}>
              Tăng dần
            </Option>
            <Option value={`Descending`}>
              Giảm dần
            </Option>
          </Select>
        </Col>
        <Col span={12} className="d-flex justify-content-end">
          <Space>
            <Link to="/admin/blog/add">
              <Button type="primary" icon={<PlusCircleOutlined />}>
                Thêm bài viết mới
              </Button>
            </Link>
            <Button
              onClick={() => {
                setIsUpdateSort(true);
              }}
              type="default"
              icon={<OrderedListOutlined />}
            >
              {"Sắp xếp"}
            </Button>
          </Space>
        </Col>
      </Row>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pagedResults.results}
        size="small"
        pagination={{
          total: pagedResults.totalItemCount,
          pageSize: pagedResults.pageSize,
          current: pagedResults.currentPage,
          onChange: gotoPage,
        }}
        loading={isLoadingList}
      ></Table>
      <Drawer
        height="70px"
        mask={false}
        placement="bottom"
        closable={true}
        title="Bạn có muốn xóa không ?"
        onClose={onEmptySelection}
        visible={
          selectedRowsKeys !== undefined &&
            selectedRowsKeys.idList !== undefined &&
            selectedRowsKeys.idList.length > 0
            ? true
            : false
        }
        extra={<Button
          type="primary"
          danger={true}
          onClick={onRemoveBtn}
          icon={<DeleteOutlined />}
        >
          Xóa {selectedRowsKeys.idList.length.toString()} dòng
        </Button>}
      >

      </Drawer>
      <Sortable
        model={
          pagedResults?.results?.length > 0
            ? pagedResults?.results?.map((item) => ({
              key: item.id.toString(),
              title: item.title,
              sort: item.sort ?? 0,
            }))
            : []
        }
        onCancel={async () => {
          setIsUpdateSort(false);
        }}
        onOk={handleSortListBlog}
        title={`Sắp xếp thứ tự sản phẩm`}
        visible={isUpdateSort}
      ></Sortable>
    </div>
  );
};

export default Blog;
